import React, { useState, useEffect, useRef } from "react";
import userImg from "../assets/admin-avatar.png";
import edit from "../assets/edit-3-svgrepo-com.svg";
import whiteedit from "../assets/editwhite.svg";
import { useAuth } from "../contexts/AuthContext";
import { formatUserAddress } from "../services/authService";

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [role, setRole] = useState("");
  const [agencyCategory, setAgencyCategory] = useState("");
  const [rating, setRating] = useState("");
  const [reviewsCount, setReviewsCount] = useState("");

  const [isEditingContact, setIsEditingContact] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [tempProfile, setTempProfile] = useState({});
  const [tempPhone, setTempPhone] = useState(phone);
  const [tempCity, setTempCity] = useState(city);

  useEffect(() => {
    if (!user) return;
    const nameParts = (user.name || "").split(" ");
    setFirstName(nameParts[0] || "");
    setLastName(nameParts.slice(1).join(" ") || "");
    setEmail(user.email || "");
    setPhone(user.phone || "");
    setCity(user.address?.city || formatUserAddress(user.address));
    setRole(user.role || "");
    setAgencyCategory(user.agencyCategory || "");
    setRating(user.rating ?? "");
    setReviewsCount(user.reviewsCount ?? "");
  }, [user]);

  const handleEditProfile = () => {
    setIsEditingProfile(true);
    setTempProfile({ firstName, lastName, email });
  };

  const handleUpdate = () => {
    setFirstName(tempProfile.firstName);
    setLastName(tempProfile.lastName);
    setEmail(tempProfile.email);
    setIsEditingProfile(false);
    if (user) {
      updateUser({
        ...user,
        name: `${tempProfile.firstName} ${tempProfile.lastName}`.trim(),
        email: tempProfile.email,
      });
    }
  };

  const handleEditContact = () => {
    setIsEditingContact(true);
    setTempPhone(phone);
    setTempCity(city);
  };

  const handleSaveContact = () => {
    setPhone(tempPhone);
    setCity(tempCity);
    setIsEditingContact(false);
    if (user) {
      updateUser({
        ...user,
        phone: tempPhone,
        address: { ...user.address, city: tempCity },
      });
    }
  };

  const [image, setImage] = useState(userImg);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user?.profileImage) setImage(user.profileImage);
  }, [user]);

  return (
    <div>
      <div className="p-6 agarly-card w-full mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold dark:text-gray-200">Profile Information</h2>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <img src={image} alt="Profile" className="w-20 h-20 rounded-agarly-lg border border-agarly-surface object-cover" />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="agarly-btn-secondary text-sm py-2"
          >
            Change Picture <img src={edit} alt="" className="w-5 h-5" />
          </button>
          <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={(e) => {
            const file = e.target.files[0];
            if (file) setImage(URL.createObjectURL(file));
          }} />
        </div>

        <div className="flex flex-wrap gap-4">
          {[
            { label: "First Name", value: firstName, editKey: "firstName" },
            { label: "Last Name", value: lastName, editKey: "lastName" },
            { label: "Email", value: email, editKey: "email" },
            { label: "Role", value: role, readOnly: true },
            { label: "Agency Category", value: agencyCategory, readOnly: true },
            { label: "Rating", value: rating, readOnly: true },
            { label: "Reviews", value: reviewsCount, readOnly: true },
          ].map((field) => (
            <div key={field.label} className="w-full md:w-[30%]">
              <label className="block text-sm font-semibold mb-2 dark:text-gray-300">{field.label}</label>
              {!isEditingProfile || field.readOnly ? (
                <p className="text-agarly-dark border border-agarly-surface p-2 rounded-agarly-md dark:border-gray-600 dark:bg-agarly-800 dark:text-gray-300">
                  {field.value || "—"}
                </p>
              ) : (
                <input
                  type="text"
                  value={tempProfile[field.editKey] || ""}
                  onChange={(e) => setTempProfile({ ...tempProfile, [field.editKey]: e.target.value })}
                  className="agarly-input"
                />
              )}
            </div>
          ))}
        </div>

        {!isEditingProfile ? (
          <button onClick={handleEditProfile} className="agarly-btn-primary mt-6">
            <img src={whiteedit} alt="" className="w-5 h-5" /> Edit
          </button>
        ) : (
          <div className="flex gap-2 mt-6">
            <button onClick={handleUpdate} className="agarly-btn-primary">Update</button>
            <button onClick={() => setIsEditingProfile(false)} className="agarly-btn-secondary">Cancel</button>
          </div>
        )}
      </div>

      <div className="p-6 agarly-card w-full">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold mb-4 dark:text-gray-300">Contact Detail</h2>
          {!isEditingContact ? (
            <button onClick={handleEditContact} className="agarly-btn-secondary text-sm py-2">
              <img src={edit} alt="" className="w-5 h-5" /> Edit
            </button>
          ) : (
            <button onClick={handleSaveContact} className="agarly-btn-primary text-sm py-2">Save</button>
          )}
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="w-full md:w-[30%]">
            <label className="block text-sm font-semibold mb-2 dark:text-gray-300">Phone Number</label>
            {!isEditingContact ? (
              <p className="text-agarly-dark bg-agarly-light/40 border border-agarly-surface p-2 rounded-agarly-md dark:bg-agarly-700 dark:text-gray-300">{phone || "—"}</p>
            ) : (
              <input type="text" value={tempPhone} onChange={(e) => setTempPhone(e.target.value)} className="agarly-input" />
            )}
          </div>
          <div className="w-full md:w-[30%]">
            <label className="block text-sm font-semibold mb-2 dark:text-gray-300">City</label>
            {!isEditingContact ? (
              <p className="text-agarly-dark bg-agarly-light/40 border border-agarly-surface p-2 rounded-agarly-md dark:bg-agarly-700 dark:text-gray-300">{city || "—"}</p>
            ) : (
              <input type="text" value={tempCity} onChange={(e) => setTempCity(e.target.value)} className="agarly-input" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
